/**
 * Radar comercial: a quién visitar y cuándo.
 *
 * Nace de una observación de campo: la etiqueta que la ley obliga a pegar en
 * cada extintor (art. 17.g RIPCI) lleva **el nombre de la mantenedora actual
 * y la fecha de la próxima revisión**. Es decir, cualquier comercial que
 * entre en un local está mirando, gratis y a la vista, quién es el
 * competidor y cuándo toca. Lo que faltaba era un sitio donde anotarlo y
 * algo que lo recordara a tiempo.
 *
 * El cálculo no cuenta desde la revisión sino desde el **preaviso**: si el
 * contrato del competidor se renueva solo salvo comunicación con dos meses
 * de antelación, presentarse a treinta días llega tarde aunque parezca que
 * sobra tiempo — el cliente ya no puede salirse. Por eso los meses de
 * preaviso son un campo del formulario y no una nota suelta.
 */

import { useCallback, useEffect, useState } from 'react';
import { hasPermission, useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';

type Window = 'ULTIMA_LLAMADA' | 'ACTUAR_YA' | 'PREPARAR' | 'VENCIDO';

interface Alert {
  leadId: string;
  companyName: string;
  competitorName: string | null;
  renewalDate: string;
  noticeDeadline: string | null;
  /** Días hasta la renovación: un recuento, no un importe. */
  daysToRenewal: number;
  window: Window;
  advice: string;
  estimatedValue: string | null;
}

interface ComplianceLead {
  leadId: string;
  companyName: string;
  priority: 'ALTA' | 'MEDIA' | 'BAJA';
  /** El argumento ya redactado, con su artículo: no hay que inventárselo. */
  argument: string;
  articleRef: string;
  estimatedValue: string | null;
}

/**
 * El orden es el del calendario, no el alfabético: lo que se cierra esta
 * semana va arriba porque es lo único que no admite espera.
 */
const WINDOWS: { id: Window; label: string; hint: string; tone: string }[] = [
  {
    id: 'ULTIMA_LLAMADA',
    label: 'Última llamada',
    hint: 'El preaviso expira en menos de 15 días: o se cierra esta semana o este año ya no cambia',
    tone: 'radar--now',
  },
  {
    id: 'ACTUAR_YA',
    label: 'Actuar ya',
    hint: 'Ventana de decisión: es cuando el cliente se plantea cambiar y hay que estar delante',
    tone: 'radar--soon',
  },
  {
    id: 'PREPARAR',
    label: 'Preparar',
    hint: 'Todavía hay margen: prepare la auditoría y el argumentario',
    tone: 'radar--prep',
  },
  {
    id: 'VENCIDO',
    label: 'Fuera de plazo',
    hint: 'Este año ya no puede cambiar. Anote la fecha del año que viene y no lo desgaste',
    tone: 'radar--late',
  },
];

export function RenewalRadarScreen(): React.ReactNode {
  const { api, user } = useSession();

  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [compliance, setCompliance] = useState<ComplianceLead[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Lo que se lee en la etiqueta, en el mismo orden en que está impresa.
  const [legalName, setLegalName] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [competitorName, setCompetitorName] = useState('');
  const [renewalDate, setRenewalDate] = useState('');
  const [noticeMonths, setNoticeMonths] = useState('');

  const canWrite = hasPermission(user, 'crm.create');

  const reload = useCallback(async () => {
    const [radar, leads] = await Promise.all([
      api.get<Alert[]>('/crm/renewal-radar').catch(() => [] as Alert[]),
      api.get<ComplianceLead[]>('/crm/compliance-leads').catch(() => [] as ComplianceLead[]),
    ]);
    setAlerts(Array.isArray(radar) ? radar : []);
    setCompliance(Array.isArray(leads) ? leads : []);
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function anotar(): Promise<void> {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await api.post('/crm/leads', {
        legalName,
        source: 'VISITA_FRIA',
        province: 'Illes Balears',
        ...(municipality === '' ? {} : { municipality }),
        ...(competitorName === '' ? {} : { competitorName }),
        ...(renewalDate === '' ? {} : { competitorRenewalDate: renewalDate }),
        ...(noticeMonths === '' ? {} : { competitorNoticeMonths: Number(noticeMonths) }),
      });
      setNotice(`Anotado: ${legalName}. Aparecerá en el radar cuando se acerque su ventana.`);
      setLegalName('');
      setMunicipality('');
      setCompetitorName('');
      setRenewalDate('');
      setNoticeMonths('');
      await reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido anotar el lead.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="screen">
      <header className="screen__head">
        <h1>Radar comercial</h1>
        <p className="screen__note">
          Lo que dice la etiqueta de un extintor ajeno: quién lo mantiene y cuándo le toca.
        </p>
      </header>

      {error ? <p className="screen__error" role="alert">{error}</p> : null}
      {notice ? <p className="screen__note screen__note--strong" role="status">{notice}</p> : null}

      {canWrite ? (
        <form
          className="card radar__form"
          onSubmit={(event) => {
            event.preventDefault();
            void anotar();
          }}
        >
          <h2>Anotar una etiqueta</h2>
          <p className="field__help">
            De una visita en frío: el nombre del local, lo que pone la etiqueta y —si se puede
            preguntar— los meses de preaviso que exige su contrato.
          </p>
          <div className="radar__fields">
            <label>
              Empresa
              <input
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                required
                placeholder="Hotel Miramar, S.L."
              />
            </label>
            <label>
              Municipio
              <input
                value={municipality}
                onChange={(e) => setMunicipality(e.target.value)}
                placeholder="Inca"
              />
            </label>
            <label>
              Mantenedora actual
              <input
                value={competitorName}
                onChange={(e) => setCompetitorName(e.target.value)}
                placeholder="Lo que figura en la etiqueta"
              />
            </label>
            <label>
              Próxima revisión
              <input
                type="date"
                value={renewalDate}
                onChange={(e) => setRenewalDate(e.target.value)}
              />
            </label>
            <label>
              Preaviso (meses)
              <input
                type="number"
                min="0"
                max="12"
                value={noticeMonths}
                onChange={(e) => setNoticeMonths(e.target.value)}
                placeholder="2"
              />
              <small className="field__help">
                Si se deja vacío se cuenta desde la propia fecha de revisión, que es optimista.
              </small>
            </label>
          </div>
          <button type="submit" className="button button--primary" disabled={busy || legalName === ''}>
            {busy ? 'Anotando…' : 'Anotar'}
          </button>
        </form>
      ) : null}

      {WINDOWS.map((w) => {
        const rows = alerts.filter((a) => a.window === w.id);
        if (rows.length === 0) return null;
        return (
          <div key={w.id} className={`card radar ${w.tone}`}>
            <h2>
              {w.label} <span className="radar__count">{rows.length}</span>
            </h2>
            <p className="field__help">{w.hint}</p>
            <table className="table__grid">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Mantenedora actual</th>
                  <th>Próxima revisión</th>
                  <th>Límite de preaviso</th>
                  <th className="table__num">Días</th>
                  <th>Qué hacer</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.leadId}>
                    <td>{a.companyName}</td>
                    <td>{a.competitorName ?? <span className="due--none">sin anotar</span>}</td>
                    <td>{a.renewalDate}</td>
                    <td>
                      {a.noticeDeadline ?? (
                        <span className="due--none">sin preaviso conocido</span>
                      )}
                    </td>
                    <td className="table__num">{a.daysToRenewal}</td>
                    <td className="field__help">{a.advice}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {alerts.length === 0 ? (
        <p className="table__empty">
          Nada en el radar. Anote la etiqueta de los locales que visite y las fechas irán
          apareciendo solas.
        </p>
      ) : null}

      {compliance.length > 0 ? (
        <div className="card radar radar--compliance">
          <h2>
            Con un problema que quizá no saben <span className="radar__count">{compliance.length}</span>
          </h2>
          <p className="field__help">
            Leads con la revisión vencida o sin contrato de mantenimiento. El art. 20.1.b exige
            contrato con empresa habilitada: no es un argumento de venta, es su obligación.
          </p>
          <table className="table__grid">
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Prioridad</th>
                <th>Qué decirle</th>
                <th>Artículo</th>
              </tr>
            </thead>
            <tbody>
              {compliance.map((l) => (
                <tr key={l.leadId}>
                  <td>{l.companyName}</td>
                  <td>
                    <span
                      className={`pill ${l.priority === 'ALTA' ? 'pill--bad' : 'pill--info'}`}
                    >
                      {l.priority}
                    </span>
                  </td>
                  <td>{l.argument}</td>
                  <td className="field__help">{l.articleRef}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
